function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
  
  // if (
  //   elementToFocus.tagName === 'INPUT' &&
  //   ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
  //   elementToFocus.value
  // ) {
  //   elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  // }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

const stackModalDialog = []; // Modal stack array (여러개의 modal을 띄워야 할 경우, 현재 열려있는 modal 갯수 체크 & 포커스 이동을 위해 사용)

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      if (!this.hasAttribute('data-close-on-backdrop-click')) {
        this.addEventListener('click', (event) => {
          if (event.target === this) this.hide();
        });
      }
    }
  }

  get isNoticePopup() {
    // 공지팝업 섹션 컴포넌트
    return this.classList.contains('notification-modal');
  }

  connectedCallback() {
    // 테마 디자인모드일 경우 dom 조작 예외 처리
    if (window.Shopify && Shopify.designMode) {
      if (this.isNoticePopup) { // 공지팝업 예외처리
        this.show();
        return;
      }
    }

    if (this.moved) return;
    this.moved = true;

    const section = this.closest('.shopify-section');
    if (section) {
      this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    }
    document.body.appendChild(this);

    // 공지팝업일 경우 opener 없이 바로 노출
    if (this.isNoticePopup) this.show();
  }

  disconnectedCallback() {
    // 테마 디자인모드 예외 처리
    if (window.Shopify && Shopify.designMode) {
      if (this.isNoticePopup) { // 공지팝업 예외처리 (ex: block 순서변경, block 숨김처리)
        this.removeFromStack(); // stack array 초기화
      }
    }
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();

    stackModalDialog.push(this); // 열린 modal을 stack array에 추가
    // console.log('show', stackModalDialog);
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();

  }

  hide() {
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    this.removeFromStack(); // stack array 조작 및 접근성 추가
    window.pauseAllMedia(); 
  }

  removeFromStack() {
    const index = stackModalDialog.indexOf(this);
    if (index !== -1) {
      stackModalDialog.splice(index, 1); // 닫힌 modal을 stack array에서 제거
      // console.log('hide', stackModalDialog);

      const stackLen = stackModalDialog.length;
      const lastStack = stackModalDialog[stackLen - 1];
      if (stackLen > 0) {
        // 팝업이 여러개 띄워저 있을 경우 다음 modal로 포커스 이동 (opener가 있으면 해당 버튼으로 이동, 없으면 닫기 버튼으로 포커스)
        const elementToFocus = (this.openedBy) ? this.openedBy : lastStack.querySelector(`[id^="ModalClose-"]`);
        trapFocus(lastStack, elementToFocus);
      } else {
        // 마지막 팝업일 경우 opener로 포커스 이동 & 스크롤 잠금 해제
        removeTrapFocus(this.openedBy);
        document.body.classList.remove('overflow-hidden');
      }
    }
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
// Custom Select
class UiSelect extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('.form-select__trigger');        // 드롭다운 토글 버튼
    this.menu = this.querySelector('.form-select__menu');              // 드롭다운 메뉴 컨테이너
    this.options = this.querySelectorAll('.form-select__item');        // 선택 가능한 옵션들
    this.hiddenSelect = this.querySelector('.form-select__hidden');    // 숨겨진 네이티브 select (폼 제출용)
    this.triggerLabel = this.querySelector('.form-select__trigger .form-select__label'); // 버튼 내 텍스트 표시 영역
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    // 드롭다운 토글 버튼 클릭 이벤트
    this.trigger.addEventListener('click', this.toggle.bind(this));
    // 각 옵션에 클릭 이벤트 추가
    this.options.forEach(option => {
      option.addEventListener('click', (event) => {
        this.selectOption(event.target);
      });
    });
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  cleanup() {
    document.removeEventListener('click', this.handleOutsideClick.bind(this));
  }

  toggle = () => {
    // 다른 열린 select들 닫기 (한 번에 하나만 열리도록)
    document.querySelectorAll('ui-select').forEach(select => {
      if (select !== this) {
        select.close();
      }
    });
    // 현재 select의 활성 상태 토글
    this.classList.toggle('form-select--active');
  }

  selectOption = (option) => {
    // option이 DOM 요소인지 확인
    if (!option || typeof option.getAttribute !== 'function') {
      console.error('Invalid option element:', option);
      return;
    }

    // disabled 상태 체크
    if (option.hasAttribute('aria-disabled') && option.getAttribute('aria-disabled') === 'true') {
      option.classList.add('form-select__item--disabled');
    }

    // 1. 버튼의 텍스트를 선택된 옵션의 텍스트로 업데이트 (sold out 제외)
    const optionTextElement = option.querySelector('.form-select__item-text');
    const optionText = optionTextElement ? optionTextElement.textContent : option.textContent;
    this.triggerLabel.textContent = optionText;

    // 2. 숨겨진 select의 값을 업데이트 (실제 폼 제출 시 사용)
    this.hiddenSelect.value = option.getAttribute('data-value');

    // (개발 추가 2025.09.20) 
    if (this.hiddenSelect && option.getAttribute('data-value')) {
      // 2-1. selected 속성 갱신
      this.hiddenSelect.querySelectorAll('option').forEach(opt => opt.removeAttribute('selected'));
      const selectedOption = this.hiddenSelect.querySelector(`option[value="${option.getAttribute('data-value')}"]`);
      if (selectedOption) selectedOption.setAttribute('selected', 'selected');
      // 2-2. JS variant 변경 로직 트리거
      const changeEvent = new Event('change', { bubbles: true });
      this.hiddenSelect.dispatchEvent(changeEvent);
    }

    // 3. 모든 옵션의 활성 상태 제거 후 선택된 옵션만 활성화
    this.options.forEach(opt => opt.classList.remove('form-select__item--selected'));
    option.classList.add('form-select__item--selected');
    // 4. 드롭다운 메뉴 닫기
    this.close();
    // 5. 커스텀 이벤트 발생
    this.dispatchEvent(new CustomEvent('change', {
      detail: {
        value: option.getAttribute('data-value'),
        text: option.textContent
      }
    }));
  }

  close = () => {
    // 드롭다운 메뉴 닫기
    this.classList.remove('form-select--active');
  }

  handleOutsideClick = (e) => {
    // 외부 클릭 감지
    if (!this.contains(e.target)) {
      this.close();
    }
  }
}

customElements.define('ui-select', UiSelect);

class UIDropdown extends HTMLElement {
  constructor() {
    super();
    this.classes = {
      trigger: "form-select__trigger",
      triggerLabel: "form-select__label",
      menu: "form-select__menu",
      options: "form-select__item",
      state: {
        active: "form-select--active",
        reverse: "form-select--reverse",
        selectedOptions: "form-select__item--selected",
        disabledOptions: "form-select__item--disabled",
        selectedLabel: "form-select__label--selected",
        disabledLabel: "form-select__label--disabled",
      }
    };
    this.trigger = this.querySelector(`.${this.classes.trigger}`);
    this.triggerLabel = this.querySelector(`.${this.classes.triggerLabel}`);
    this.menu = this.querySelector(`.${this.classes.menu}`);
    this.onKeyDownMenuHandle = this.onKeyDownMenu.bind(this);
    this.checkedLabel = '';
    this.checkedValue = null;
  }

  get selectMode() {
    return this.hasAttribute("select");
  }

  connectedCallback() {
    this.setValue();
    document.addEventListener("mousedown", this.onLeave.bind(this));
    this.addEventListener("click", this.onEnter.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener("mousedown", this.onLeave.bind(this));
  }


  open(a11y, callback) {
    const isClipped = () => {
      // 레이어 하단이 본문 영역에서 짤리는지 체크
      return (window.scrollY + this.menu.getBoundingClientRect().bottom) >= document.documentElement.scrollHeight;
    };

    const onOpenEnd = () => {
      this.classList.add(this.classes.state.active);
      this.trigger.setAttribute("aria-expanded", true);
      this.menu.offsetHeight;

      if (a11y) {
        const prev = this.menu.querySelector(`li:has(.${this.classes.state.selectedOptions})`) ?? this.menu.querySelector(`li:first-child`);
        if (prev) prev.querySelector("a, input").focus(); // 선택된 항목으로 포커스

        this.menu.addEventListener("keydown", this.onKeyDownMenuHandle);
      }
    };

    onOpenEnd();

    if (isClipped()) {
      this.classList.add(this.classes.state.reverse);
    }

    if (callback && typeof callback === "function") callback();
  }

  close(a11y, callback) {
    const onCloseEnd = () => {
      this.classList.remove(this.classes.state.active, this.classes.state.reverse);

      if (a11y) {
        this.menu.removeEventListener("keydown", this.onKeyDownMenuHandle);
        this.trigger.setAttribute("aria-expanded", false);
        this.trigger.focus();
      }
    };

    onCloseEnd();

    if (callback && typeof callback === "function") callback();
  }

  onKeyDownMenu(e) {
    const items = Array.from(this.menu.querySelectorAll("li"));
    const focusedElement = e.target.closest("li");
    const currentIndex = focusedElement ? items.indexOf(focusedElement) : -1;

    const focusPrevItem = () => {
      e.preventDefault();
      if (currentIndex > 0) {
        const prevItem = items[currentIndex - 1];
        const prevFocusElement = prevItem.querySelector("a, input");
        prevFocusElement?.focus();
      }
    };

    const focusNextItem = () => {
      e.preventDefault();
      if (currentIndex < items.length - 1) {
        const nextItem = items[currentIndex + 1];
        const nextFocusElement = nextItem.querySelector("a, input");
        nextFocusElement?.focus();
      }
    };

    switch (e.key) {
      case "Escape":
        e.preventDefault();
        this.close(true);
        break;

      case "ArrowDown":
        focusNextItem();
        break;

      case "ArrowUp":
        focusPrevItem();
        break;

      case "Tab":
        const lastItem = items[items.length - 1];
        const firstItem = items[0];

        if (e.shiftKey) {
          focusPrevItem();
          if (focusedElement === firstItem) this.close(true);
        } else if (!e.shiftKey) {
          focusNextItem();
          if (focusedElement === lastItem) this.close(true);
        }
        break;

      case "Enter":
      case " ": // Spacebar
        if (focusedElement?.querySelector("input")) {
          e.preventDefault();
          this.onEnter(e);
        }
        break;

      default:
        break;
    }
  }

  onEnter(e) {
    const target = e.target;
    const targetTrigger = target.closest(`.${this.classes.trigger}`);
    const targetOptions = (e.type === "keydown") ? target.parentElement.querySelector(`.${this.classes.options}`) : target.closest(`.${this.classes.options}`);

    if (targetTrigger) {
      const isActiveTrigger = this.classList.contains(this.classes.state.active);
      if (isActiveTrigger) {
        this.close(true);
      } else {
        this.open(true);
      }

      return;
    }

    if (this.selectMode && targetOptions) {
      const prev = this.menu.querySelector(`.${this.classes.state.selectedOptions}`);
      const isDisabledInput = targetOptions.closest("li:has(input:disabled)");
      const isSelected = targetOptions.classList.contains(this.classes.state.selectedOptions);

      if (prev) this.resetValue(prev);

      if (isDisabledInput) return;

      if (!isSelected) {
        targetOptions.classList.add(this.classes.state.selectedOptions);
        this.setValue(targetOptions);

        // 커스텀 이벤트 추가
        this.dispatchChangeEvent(targetOptions);
      }

      this.close(true);
    }
  }

  onLeave(e) {
    const target = e.target;
    if (e.button === 0) {
      // 바닥 페이지 왼클릭시 닫히게
      if (!this.contains(target) && this.classList.contains(this.classes.state.active)) {
        this.close(false);
      }
    }
  }

  setValue(item) {
    // select mode 이고 선택된 옵션값이 있을때 버튼명 변경
    const selected = item ? item : this.menu.querySelector(`.${this.classes.state.selectedOptions}`);

    if (this.selectMode && selected) {
      const isDisabled = selected.classList.contains(this.classes.state.disabledOptions);
      this.checkedLabel = selected.textContent;
      this.triggerLabel.innerHTML = selected.innerHTML;
      this.triggerLabel.classList.add(this.classes.state.selectedLabel);
      if (isDisabled) {
        this.triggerLabel.classList.add(this.classes.state.disabledLabel);
      }

      const input = selected.parentElement.querySelector("input");
      if (input && !input.disabled) {
        // (참고) input 관련 로직 변경시 PDP 상단 Variant picker 내에서도 테스트 필요
        if (input.checked) {
          this.checkedValue = input.value;
          console.log(`input checkedValue : ${this.checkedValue}`);
        } else {
          input.click();
        }
      }
    }
  }

  resetValue(item) {
    item.classList.remove(this.classes.state.selectedOptions);
    this.triggerLabel.classList.remove(this.classes.state.selectedLabel, this.classes.state.disabledLabel);

    const input = item.parentElement.querySelector("input");
    if (input) {
      input.checked = false;
    }
  }

  dispatchChangeEvent(item) {
    const customEvent = new CustomEvent("ui-dropdown:change", {
      detail: {
        index: Array.from(this.menu.querySelectorAll("li")).indexOf(item.parentElement),
        indexElement: item.parentElement,
        dataElement: item.parentElement.querySelector("a, input"),
        label: this.checkedLabel,
        value: this.checkedValue,
      },
      bubbles: false
    });

    this.dispatchEvent(customEvent);
  }
}

customElements.define("ui-dropdown", UIDropdown);

class UiTabTrigger extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('button[data-tab]');
    this.activeTab = this.tabs[0]?.dataset.tab;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // 탭 클릭 이벤트
    this.addEventListener('click', (e) => {
      const clickedTab = e.target.closest('button[data-tab]');
      if (clickedTab) {
        this.switchTab(clickedTab.dataset.tab);
      }
    });

    // 첫 번째 탭 활성화
    if (this.activeTab) {
      this.switchTab(this.activeTab);
    }
  }

  switchTab(tabId) {
    // 모든 탭 비활성화
    this.tabs.forEach(tab => {
      tab.classList.remove('ui-tab--active');
    });

    // 선택된 탭 활성화
    const activeTab = this.querySelector(`button[data-tab="${tabId}"]`);
    if (activeTab) {
      activeTab.classList.add('ui-tab--active');
      this.activeTab = tabId;

      // 컨테이너로 이벤트 전달
      this.dispatchEvent(new CustomEvent('tab-change', {
        detail: { activeTab: tabId },
        bubbles: true
      }));
    }
  }
}

class UiTabContainer extends HTMLElement {
  constructor() {
    super();
    // 탭 패널들 수집
    this.panels = Array.from(this.children).filter(child =>
      child.classList.contains('ui-tab-panel')
    );
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // 첫 번째 패널만 활성화
    this.panels.forEach((panel, index) => {
      if (index === 0) {
        panel.classList.add('ui-tab-panel--active');
        panel.hidden = false;
      } else {
        panel.classList.remove('ui-tab-panel--active');
        panel.hidden = true;
      }
    });

    // 탭 변경 이벤트 수신
    this.addEventListener('tab-change', (e) => {
      if (e.target.parentElement === this) {
        this.updatePanels(e.detail.activeTab);
      }
    });
  }

  updatePanels(activeTab) {
    // 해당하는 패널만 보이기
    this.panels.forEach(panel => {
      const panelTab = panel.getAttribute('data-tab');
      if (panelTab === activeTab) {
        panel.classList.add('ui-tab-panel--active');
        panel.hidden = false;
      } else {
        panel.classList.remove('ui-tab-panel--active');
        panel.hidden = true;
      }
    });
  }
}

customElements.define('ui-tab-trigger', UiTabTrigger);
customElements.define('ui-tab-container', UiTabContainer);

class UISlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.swiperContainer = this.querySelector(".swiper");
    // isPlaying 상태 저장
    this.isPlaying = true;
    // autoplay가 수동으로 중지되었는지 추적 (사용자가 버튼으로 중지한 경우)
    this.autoplayManuallyStopped = false;
    // autoplay toggle 버튼 클릭 이벤트 리스너 참조 (중복 등록 방지용)
    this.autoplayToggleHandler = null;
  }

  connectedCallback() {
    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!this.swiper) {
              this.initSlider();
            } else {
              this.swiper.update();
              if (this.swiper.navigation) {
                this.swiper.navigation.update();
              }
            }
          }
        });
      },
      { rootMargin: "0px 0px 200px 0px" },
    );

    this.intersectionObserver = observer;
    observer.observe(this);
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    // 탭 변경 이벤트 리스너 정리
    if (this.tabChangeHandlers) {
      this.tabChangeHandlers.forEach(({ container, handler }) => {
        container.removeEventListener("tab-change", handler);
      });
      this.tabChangeHandlers = null;
    }

    // autoplay toggle 버튼 이벤트 리스너 정리
    if (this.autoplayToggleHandler) {
      const toggleButton = this.querySelector(".swiper-button-play");
      if (toggleButton) {
        toggleButton.removeEventListener("click", this.autoplayToggleHandler);
      }
      this.autoplayToggleHandler = null;
    }

    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = null;
    }
  }

  initSlider() {
    const optionsAttribute = this.dataset.options;
    let swiperOptions = {};

    try {
      if (optionsAttribute) {
        swiperOptions = JSON.parse(optionsAttribute);
      }
    } catch (e) {
      console.error("Failed to parse slider options for:", this, e);
      swiperOptions = { slidesPerView: 1 };
    }

    const baseOptions = {
      navigation: {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      },
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      },
      on: {
        slideChange: (swiper) => {
          this.dispatchEvent(
            new CustomEvent("ui-slider:slide-change", {
              detail: { swiper },
            }),
          );
        },
        activeIndexChange: (swiper) => {
          this.dispatchEvent(
            new CustomEvent("ui-slider:activeIndexChange", {
              detail: { swiper },
            }),
          );
          swiperOptions.on?.activeIndexChange?.(swiper);
        },
        init: (swiper) => {
          this.handlePagination(swiper);
          this.handleLockedState(swiper);
          this.updateNavPosition();

          this.dispatchEvent(
            new CustomEvent("ui-slider:init", { detail: { swiper } }),
          );
        },
        update: (swiper) => {
          this.handlePagination(swiper);
          this.handleLockedState(swiper);
          this.updateNavPosition();
          this.dispatchEvent(
            new CustomEvent("ui-slider:update", { detail: { swiper } }),
          );
          swiperOptions.on?.update?.(swiper);
        },
        destroy: (swiper) => {
          this.dispatchEvent(
            new CustomEvent("ui-slider:destroy", { detail: { swiper } }),
          );
          swiperOptions.on?.destroy?.(swiper);
        },
        resize: (swiper) => {
          this.handlePagination(swiper);
          this.handleLockedState(swiper);
          this.updateNavPosition();
          this.dispatchEvent(
            new CustomEvent("ui-slider:resize", { detail: { swiper } }),
          );
          swiperOptions.on?.resize?.(swiper);
        },
        click: (swiper, event) => {
          this.dispatchEvent(
            new CustomEvent("ui-slider:click", {
              detail: { swiper, originalEvent: event },
            }),
          );
          swiperOptions.on?.click?.(swiper, event);
        },
        breakpoints: (swiper) => {
          this.handlePagination(swiper);
          this.handleLockedState(swiper);
          this.updateNavPosition();
          this.dispatchEvent(
            new CustomEvent("ui-slider:breakpoints", { detail: { swiper } }),
          );
          swiperOptions.on?.breakpoints?.(swiper);
        },
        autoplayStart: (swiper) => {
          // Swiper autoplay가 시작될 때 상태 동기화
          // 수동으로 멈춘 상태가 아니면 isPlaying을 true로 설정
          if (!this.autoplayManuallyStopped) {
            this.isPlaying = true;
            // 버튼 상태도 동기화
            const toggleButton = this.querySelector(".swiper-button-play");
            if (toggleButton) {
              toggleButton.classList.add("is-playing");
              toggleButton.setAttribute("aria-label", "Autoplay Pause");
              toggleButton.setAttribute("aria-pressed", "true");
            }
          }
          swiperOptions.on?.autoplayStart?.(swiper);
        },
        autoplayStop: (swiper) => {
          // Swiper autoplay가 중지될 때 상태 동기화
          // 수동으로 멈춘 상태가 아니면 isPlaying을 false로 설정
          if (!this.autoplayManuallyStopped) {
            this.isPlaying = false;
            // 버튼 상태도 동기화
            const toggleButton = this.querySelector(".swiper-button-play");
            if (toggleButton) {
              toggleButton.classList.remove("is-playing");
              toggleButton.setAttribute("aria-label", "Autoplay Play");
              toggleButton.setAttribute("aria-pressed", "false");
            }
          }
          swiperOptions.on?.autoplayStop?.(swiper);
        },
      },
    };

    const finalOptions = {
      ...baseOptions,
      ...swiperOptions,
      on: { ...baseOptions.on, ...swiperOptions.on },
    };

    this.swiper = new Swiper(this.swiperContainer, finalOptions);
    // autoplay 토글 버튼 설정
    this.setupAutoplayToggle();

    // 탭 전환 이벤트 감지 추가 (swiper 초기화 후에만 실행)
    if (!this.tabChangeHandlers) {
      this.setupTabChangeListener();
    }
  }

  setupTabChangeListener() {
    // 이미 설정되어 있으면 중복 실행 방지
    if (this.tabChangeHandlers) {
      return;
    }

    // ui-tab-panel의 자식 요소인지 확인 (탭 UI에 속하지 않은 swiper는 리스너 불필요)
    let element = this;
    let isInsideTabPanel = false;
    let depth = 0;
    const maxDepth = 3;
    while (element && element !== document.body && depth < maxDepth) {
      if (element.classList && element.classList.contains("ui-tab-panel")) {
        isInsideTabPanel = true;
        break;
      }
      element = element.parentElement;
      depth++;
    }

    // 탭 패널 내부에 있지 않으면 리스너 설정 불필요
    if (!isInsideTabPanel) {
      return;
    }

    // 모든 상위 탭 컨테이너 찾기 (최대 2중 탭 구조 대응)
    const allTabContainers = [];
    let currentElement = this.parentElement;
    let containerDepth = 0;
    const maxTabContainerDepth = 2; // 최대 2중 탭 구조
    const maxDepthLimit = 20; // 안전장치: 충분한 depth 허용

    // DOM 트리를 올라가면서 최대 2개의 ui-tab-container 찾기
    while (currentElement && containerDepth < maxDepthLimit && currentElement !== document.body) {
      if (currentElement.tagName === "UI-TAB-CONTAINER") {
        allTabContainers.push(currentElement);
        // 최대 2개를 찾으면 즉시 종료
        if (allTabContainers.length >= maxTabContainerDepth) {
          break;
        }
      }
      currentElement = currentElement.parentElement;
      containerDepth++;
    }

    if (allTabContainers.length === 0) return;

    // 각 탭 컨테이너에 이벤트 리스너 추가
    this.tabChangeHandlers = [];

    allTabContainers.forEach((tabContainer) => {
      const handler = () => {
        // daily-ranking-product-slider 클래스가 있는 경우만 재초기화
        // (tab1-slider, tab-type-2-slider는 제외)
        const shouldReinit = this.classList.contains("daily-ranking-product-slider");
        
        if (!shouldReinit) {
          return; // 재초기화가 필요하지 않은 슬라이더는 건너뛰기
        }

        // 현재 슬라이더가 속한 모든 탭 패널 확인
        const allTabPanels = [];
        let element = this;

        // 현재 슬라이더부터 상위로 올라가면서 모든 ui-tab-panel 찾기
        while (element && element !== document.body) {
          if (element.classList && element.classList.contains("ui-tab-panel")) {
            allTabPanels.push(element);
          }
          element = element.parentElement;
        }

        // 모든 탭 패널이 visible 상태인지 확인
        const isVisible = allTabPanels.every((panel) => !panel.hidden);

        if (isVisible && this.swiper) {
          // 약간의 지연 후 재초기화 (DOM 업데이트 대기)
          setTimeout(() => {
            this.reinitializeSwiper();
          }, 50);
        }
      };

      tabContainer.addEventListener("tab-change", handler);
      this.tabChangeHandlers.push({ container: tabContainer, handler });
    });
  }


  reinitializeSwiper() {
    if (!this.swiper) return;

    // 현재 활성 인덱스 저장 (복원용)
    const currentIndex = this.swiper.params.loop
      ? this.swiper.realIndex !== undefined
        ? this.swiper.realIndex
        : 0
      : this.swiper.activeIndex !== undefined
        ? this.swiper.activeIndex
        : 0;

    // 재초기화 전 autoplay 옵션 존재 여부 확인
    const hadAutoplayBefore = this.swiper.params.autoplay && 
                               this.swiper.params.autoplay !== false && 
                               this.swiper.autoplay;

    // autoplay 상태 저장 (복원용) - autoplay가 있었을 때만 저장
    const savedAutoplayState = hadAutoplayBefore ? {
      isPlaying: this.isPlaying,
      autoplayManuallyStopped: this.autoplayManuallyStopped,
    } : null;

    // Swiper destroy
    try {
      this.swiper.destroy(true, true);
    } catch (e) {
      console.error("Error destroying swiper:", e);
    }
    this.swiper = null;

    // 약간의 지연 후 재초기화
    setTimeout(() => {
      this.initSlider();

      // 재초기화 후 autoplay 옵션 존재 여부 확인
      const hasAutoplayAfter = this.swiper && 
                                this.swiper.params.autoplay && 
                                this.swiper.params.autoplay !== false && 
                                this.swiper.autoplay;

      // autoplay가 재초기화 전후 모두 있을 때만 상태 복원
      if (hadAutoplayBefore && hasAutoplayAfter && savedAutoplayState) {
        // 저장된 autoplay 상태 복원
        this.isPlaying = savedAutoplayState.isPlaying;
        this.autoplayManuallyStopped = savedAutoplayState.autoplayManuallyStopped;

        // autoplay 상태에 따라 실제 Swiper autoplay 동기화
        if (this.autoplayManuallyStopped && this.swiper.autoplay.running) {
          // 수동으로 멈춘 상태였다면 autoplay 중지
          this.swiper.autoplay.stop();
        } else if (!this.autoplayManuallyStopped && !this.swiper.autoplay.running) {
          // 자동 재생 상태였다면 autoplay 시작
          this.swiper.autoplay.start();
        }

        // 버튼 상태 동기화
        const toggleButton = this.querySelector(".swiper-button-play");
        if (toggleButton) {
          toggleButton.classList.toggle("is-playing", this.isPlaying);
          toggleButton.setAttribute("aria-label", this.isPlaying ? "Autoplay Pause" : "Autoplay Play");
          toggleButton.setAttribute("aria-pressed", this.isPlaying ? "true" : "false");
        }
      } else {
        // autoplay가 없었거나 재초기화 후에도 없으면 상태 초기화
        this.isPlaying = true;
        this.autoplayManuallyStopped = false;
      }

      // 재초기화 후 저장된 인덱스로 이동 (애니메이션 없이)
      if (this.swiper && currentIndex < this.swiper.slides.length) {
        setTimeout(() => {
          if (this.swiper) {
            const isLoopMode = this.swiper.params.loop;
            if (isLoopMode) {
              this.swiper.slideToLoop(currentIndex, 0);
            } else {
              this.swiper.slideTo(currentIndex, 0);
            }
          }
        }, 50);
      } else {
      }
    }, 50);
  }

  setupAutoplayToggle() {
    const toggleButton = this.querySelector(".swiper-button-play");

    // autoplay 옵션이 없거나(false), autoplay 모듈이 초기화되지 않았거나, 버튼이 없으면 함수를 종료합니다.
    if (
      !this.swiper.params.autoplay ||
      this.swiper.params.autoplay === false ||
      !this.swiper.autoplay ||
      !toggleButton
    ) {
      if (toggleButton) toggleButton.style.display = "none"; // autoplay가 없으면 버튼 숨김
      return;
    }

    // 기존 이벤트 리스너가 있으면 제거 (중복 등록 방지)
    if (this.autoplayToggleHandler) {
      toggleButton.removeEventListener("click", this.autoplayToggleHandler);
      this.autoplayToggleHandler = null;
    }

    // 실제 Swiper autoplay 상태와 동기화
    if (this.swiper.autoplay.running !== undefined) {
      this.isPlaying = this.swiper.autoplay.running;
    }

    // 버튼 상태를 현재 isPlaying 상태로 설정
    toggleButton.classList.toggle("is-playing", this.isPlaying);
    toggleButton.setAttribute("aria-label", this.isPlaying ? "Autoplay Pause" : "Autoplay Play");
    toggleButton.setAttribute("aria-pressed", this.isPlaying ? "true" : "false");

    // 이벤트 리스너 생성 및 저장
    this.autoplayToggleHandler = () => {
      if (this.isPlaying) {
        this.swiper.autoplay.stop();
        this.isPlaying = false;
        this.autoplayManuallyStopped = true; // 수동으로 중지함을 표시
        toggleButton.setAttribute("aria-label", "Autoplay Play");
        toggleButton.setAttribute("aria-pressed", "false");
      } else {
        this.swiper.autoplay.start();
        this.isPlaying = true;
        this.autoplayManuallyStopped = false; // 수동 중지 플래그 해제
        toggleButton.setAttribute("aria-label", "Autoplay Pause");
        toggleButton.setAttribute("aria-pressed", "true");
      }
      toggleButton.classList.toggle("is-playing", this.isPlaying);
    };

    toggleButton.addEventListener("click", this.autoplayToggleHandler);
  }

  // swiper-button-prev,next position update
  updateNavPosition() {
    const firstSlide = this.querySelector(".swiper-slide");
    if (!firstSlide) return;

    // card__inner 또는 card__media 요소를 찾아서 높이 계산
    const cardInner = firstSlide.querySelector(".card__inner");
    const cardMedia = firstSlide.querySelector(".card__media");
    const firstSlideImg = firstSlide.querySelector("img");
    
    if (!firstSlideImg) return;

    // 이미지 로드 후 실행
    if (!firstSlideImg.complete) {
      firstSlideImg.addEventListener("load", () => this.updateNavPosition(), {
        once: true,
      });
      return;
    }

    // card__inner 또는 card__media의 높이를 우선 사용, 없으면 이미지 높이 사용
    let targetHeight = 0;
    if (cardInner && cardInner.offsetHeight > 0) {
      targetHeight = cardInner.offsetHeight;
    } else if (cardMedia && cardMedia.offsetHeight > 0) {
      targetHeight = cardMedia.offsetHeight;
    } else if (firstSlideImg.offsetHeight > 0) {
      targetHeight = firstSlideImg.offsetHeight;
    } else {
      // 높이가 0이면 재시도
      setTimeout(() => this.updateNavPosition(), 100);
      return;
    }

    const halfHeight = targetHeight / 2;
    const prevBtn = this.querySelector(".swiper-button-prev");
    const nextBtn = this.querySelector(".swiper-button-next");

    if (prevBtn) {
      prevBtn.style.top = `${halfHeight}px`;
    }
    if (nextBtn) {
      nextBtn.style.top = `${halfHeight}px`;
    }
  }
  // pagination 처리
  handlePagination(swiper) {
    let paginationEl = this.querySelector(".swiper-pagination");
    const isPaginationEnabled =
      swiper.params.pagination && swiper.params.pagination.enabled;

    // Check if pagination is enabled in the CURRENT active parameters
    if (isPaginationEnabled) {
      // 페이지네이션 엘리먼트가 없으면 생성
      if (!paginationEl) {
        paginationEl = document.createElement("div");
        paginationEl.className = "swiper-pagination";
        this.appendChild(paginationEl);
        swiper.params.pagination.el = paginationEl;
      } else {
        // 이미 paginationEl이 있을 경우, swiper에 연결 및 초기화
        swiper.params.pagination.el = paginationEl;
      }
      swiper.pagination.init();
      swiper.pagination.render();
      swiper.pagination.update();
    } else {
      if (paginationEl) {
        paginationEl.remove();
        swiper.pagination.destroy();
        swiper.update();
        swiper.resize && swiper.resize();
      }
    }
  }

  // isLocked 상태에 따른 pagination과 navigation 버튼 숨김 처리
  handleLockedState(swiper) {
    const prevBtn = this.querySelector(".swiper-button-prev");
    const nextBtn = this.querySelector(".swiper-button-next");
    const playBtn = this.querySelector(".swiper-button-play");
    const pagination = this.querySelector(".swiper-pagination");

    if (swiper.isLocked) {
      prevBtn && prevBtn.classList.add("swiper-button-hidden");
      nextBtn && nextBtn.classList.add("swiper-button-hidden");
      playBtn && playBtn.classList.add("swiper-button-hidden");
      pagination && pagination.classList.add("swiper-pagination-hidden");
    } else {
      prevBtn && prevBtn.classList.remove("swiper-button-hidden");
      nextBtn && nextBtn.classList.remove("swiper-button-hidden");
      playBtn && playBtn.classList.remove("swiper-button-hidden");
      pagination && pagination.classList.remove("swiper-pagination-hidden");
    }
  }
}

customElements.define('ui-slider', UISlider); 

class LayerTooltip extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('.layer-tooltip__trigger');
    this.content = this.querySelector('.layer-tooltip__content');

    this.init();
  }

  init() {
    // 트리거 클릭 시 토글
    this.trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });

    // 외부 클릭 감지
    document.addEventListener('click', this.handleOutsideClick.bind(this));

    // 링크 클릭 시 닫기
    this.content.addEventListener('click', (e) => {
      if (e.target.tagName === 'A') {
        this.close();
      }
    });
  }

  handleOutsideClick = (e) => {
    // layer-tooltip 외부 클릭 시 닫기
    if (!this.contains(e.target)) {
      this.close();
    }
  }

  toggle = () => {
    const isOpen = this.classList.contains('open');
    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open = () => {
    this.classList.add('open');
    this.trigger.setAttribute('aria-expanded', true);
    this.content.setAttribute('aria-hidden', false);
  }

  close = () => {
    this.classList.remove('open');
    this.trigger.setAttribute('aria-expanded', false);
    this.content.setAttribute('aria-hidden', true);
  }
}

customElements.define('layer-tooltip', LayerTooltip);
